import React, { useState, useEffect } from 'react'
import Loading from './Loading'

interface ImageSlicerProps {
  imageUrl: string
  staticImageUrl?: string // Optional static image URL for the static phase
  sliceCount?: number
  offsetAmount?: number
  animationDuration?: number
  pauseDuration?: number
}

const ImageSlicer: React.FC<ImageSlicerProps> = ({
  imageUrl,
  staticImageUrl,
  sliceCount = 4,
  offsetAmount = 30,
  animationDuration = 1000, // Adjusted to match the animation duration
  pauseDuration = 2500, // Adjusted to match the static image duration
}) => {
  const [loaded, setLoaded] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isStatic, setIsStatic] = useState(false) // State for static display
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const img = new Image()
    img.onload = () => {
      setDimensions({ width: img.width, height: img.height })
      setLoaded(true)
    }
    img.src = imageUrl
  }, [imageUrl])

  useEffect(() => {
    if (loaded) {
      const animate = () => {
        setIsAnimating(true)
        setIsStatic(false) // Ensure we're not in static mode during animation
        setTimeout(() => {
          setIsAnimating(false)
          if (staticImageUrl) {
            setIsStatic(true) // Switch to static display if staticImageUrl is provided
          } else {
            // Pause for the specified duration if no static image is provided
            setTimeout(() => {
              animate() // Restart the animation after the pause duration
            }, pauseDuration)
          }
        }, animationDuration)
      }

      animate()

      const interval = setInterval(() => {
        animate()
      }, animationDuration + pauseDuration)

      return () => clearInterval(interval)
    }
  }, [loaded, animationDuration, pauseDuration, staticImageUrl])

  if (!loaded)
    return (
      <div className=' w-full h-full flex justify-center items-center pt-20'>
        <Loading
          classes={
            'flex-grow flex relative max-h-screen sm:min-h-[645px] bg-[rgba(128,128,128,0.9)] !text-white'
          }
        />
      </div>
    )

  const sliceWidth = dimensions.width / sliceCount
  const slices = Array.from({ length: sliceCount }, (_, index) => {
    const isEven = index % 2 === 0
    const offset = isAnimating ? (isEven ? offsetAmount : -offsetAmount) : 0
    const bgOffset = -(index * sliceWidth)

    // Common styles for both original and duplicate slices
    const commonStyles = {
      backgroundImage: `url(${imageUrl})`,
      backgroundPosition: `${bgOffset}px 0`,
      backgroundSize: `${dimensions.width}px ${dimensions.height}px`,
      transition: `all ${animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    }

    // Calculate exact positions for duplicates
    const topDuplicateOffset = -dimensions.height + offset
    const bottomDuplicateOffset = dimensions.height + offset

    return (
      <div
        key={index}
        className='absolute h-full overflow-hidden'
        style={{
          left: `${(index * 100) / sliceCount}%`,
          width: `${100 / sliceCount}%`,
          height: '100%',
        }}
      >
        {/* Original slice */}
        <div
          className='absolute h-full w-full'
          style={{
            ...commonStyles,
            transform: `translateY(${offset}px)`,
            opacity: isStatic ? 0 : 1, // Hide original slices when isStatic is true
            zIndex: 2,
          }}
        />

        <div
          className='absolute h-full w-full'
          style={{
            ...commonStyles,
            transform: `translateY(${
              isEven ? bottomDuplicateOffset : topDuplicateOffset
            }px)`,
            opacity: isAnimating ? 0.9 : 0,
            filter: 'blur(0.8px)',
            zIndex: 1,
          }}
        />

        <div
          className='absolute h-full w-full'
          style={{
            ...commonStyles,
            transform: `translateY(${
              isEven ? topDuplicateOffset : bottomDuplicateOffset
            }px)`,
            opacity: isAnimating ? 0.9 : 0,
            filter: 'blur(0.5px)',
            zIndex: 1,
          }}
        />
      </div>
    )
  })

  return (
    <div className='flex flex-col items-center gap-4'>
      <div
        className='relative overflow-hidden'
        style={{
          width: dimensions.width,
          height: dimensions.height,
        }}
      >
        <div className='relative w-full h-full'>
          {slices}

          {/* Static Image Slice (Only if staticImageUrl is provided) */}
          {staticImageUrl && (
            <div
              className='absolute h-full w-full'
              style={{
                backgroundImage: `url(${staticImageUrl})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                opacity: isStatic ? 1 : 0, // Display static image when isStatic is true
                transition: `opacity ${animationDuration}ms ease-out`, // Smooth transition of opacity
                zIndex: 1,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageSlicer
